import React, { useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_CATERING_INQUIRY_QUESTIONS,
  DEFAULT_EVENT_INQUIRY_QUESTIONS,
} from '../../../types/onboardingIntake';
import { Button, IconButton } from '../../../ui/v2';
import { Callout, Reason } from '../../../ui/v2';
import { Input } from '../../../ui/v2';
import { SaveBar } from '../../../ui/v2';
import { ReorderableList } from '../../../ui/v2';
import { Switch } from '../../../ui/v2';
import { IconGrip, IconPlus, IconTrash } from '../../../ui/v2';
import { useStoreActions, useStoreInfoQuery } from './data';
import {
  type StoreInfoData,
  MAX_CUSTOM_QUESTIONS,
  normalizeQuestions,
} from './model';
import {
  Block,
  BlockSkeleton,
  QueryGate,
  SavedNote,
  SectionBody,
  SectionHead,
  useSavedAt,
} from './shared';

/**
 * Events & catering.
 *
 * Two blocks with the same shape: a Switch that saves immediately (Arthur
 * starts or stops taking that kind of request on the next call), and — while
 * on — the ordered list of questions Arthur asks. The name-and-contact
 * question is pinned: the agent always asks it, so it is not saved. An empty
 * saved list is the backend's default set, shown here so it can be edited.
 * Questions are a draft: reorder, edit, remove and add collect in one SaveBar
 * and save as one PUT with only the lists that changed.
 */

type Kind = 'events' | 'catering';

/** Always asked. Not part of the saved list. */
const LOCKED_QUESTION =
  'Can you please provide your name and preferred contact method?';
const LOCKED_DELETE_REASON = 'these questions cannot be deleted';

/** What the inquiry tool asks when the store has not saved its own list. */
const DEFAULT_QUESTIONS: Record<Kind, string[]> = {
  events: DEFAULT_EVENT_INQUIRY_QUESTIONS,
  catering: DEFAULT_CATERING_INQUIRY_QUESTIONS,
};

const shownQuestions = (kind: Kind, saved: string[]): string[] =>
  saved.length > 0 ? saved : DEFAULT_QUESTIONS[kind];

const KIND = {
  events: {
    title: 'Questions your agent will ask for events',
    off: 'Turn this on to let Arthur take event requests on calls.',
    switchLabel: 'Take event requests',
    field: 'event_inquiry_questions' as const,
    flag: 'takes_events' as const,
    placeholder: 'Add additional questions here',
    noun: 'event',
  },
  catering: {
    title: 'Questions your agent will ask for catering',
    off: 'Turn this on to let Arthur take catering requests on calls.',
    switchLabel: 'Take catering requests',
    field: 'catering_inquiry_questions' as const,
    flag: 'takes_catering' as const,
    placeholder: 'Add additional questions here',
    noun: 'catering',
  },
};

type Item = { key: number; text: string };
let nextKey = 1;
const toItems = (qs: string[]): Item[] =>
  qs.map((text) => ({ key: nextKey++, text }));

const EventsSection: React.FC = () => {
  const info = useStoreInfoQuery(
    'Event and catering settings could not be loaded.',
  );
  const storeActions = useStoreActions();
  const saved = info.data;

  const [drafts, setDrafts] = useState<Record<Kind, Item[]>>({
    events: [],
    catering: [],
  });
  const [invalidKeys, setInvalidKeys] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, markSaved] = useSavedAt();

  // Keyed on content, not reference: toggling a switch re-fetches the store
  // (new arrays, same questions) and must not wipe an in-progress draft.
  const savedKey = JSON.stringify([
    normalizeQuestions(saved?.event_inquiry_questions),
    normalizeQuestions(saved?.catering_inquiry_questions),
  ]);
  const savedLists = useMemo(() => {
    const [events, catering] = JSON.parse(savedKey) as [string[], string[]];
    return { events, catering };
  }, [savedKey]);
  useEffect(() => {
    setDrafts({
      events: toItems(shownQuestions('events', savedLists.events)),
      catering: toItems(shownQuestions('catering', savedLists.catering)),
    });
    setInvalidKeys(new Set());
    setError(null);
  }, [savedLists]);

  const changedKinds = (['events', 'catering'] as Kind[]).filter(
    (k) =>
      saved?.[KIND[k].flag] &&
      JSON.stringify(drafts[k].map((i) => i.text)) !==
        JSON.stringify(shownQuestions(k, savedLists[k])),
  );
  const dirty = changedKinds.length > 0;

  const save = async () => {
    const blanks = changedKinds.flatMap((k) =>
      drafts[k].filter((i) => !i.text.trim()).map((i) => i.key),
    );
    if (blanks.length) {
      setInvalidKeys(new Set(blanks));
      setError(
        blanks.length === 1
          ? 'One question is empty. Fill it in or remove it.'
          : `${blanks.length} questions are empty. Fill them in or remove them.`,
      );
      return;
    }
    setSaving(true);
    setError(null);
    const patch: Partial<StoreInfoData> = {};
    changedKinds.forEach((k) => {
      patch[KIND[k].field] = drafts[k].map((i) => i.text.trim());
    });
    try {
      await storeActions.update(patch);
      // An empty stored list is the backend default set. Saving "no extra
      // questions" writes [] again, so savedKey does not change and the
      // effect above never reseeds. Put the defaults back or the SaveBar
      // stays dirty against those rows.
      setDrafts((current) => {
        const next = { ...current };
        changedKinds.forEach((k) => {
          const written = patch[KIND[k].field];
          if (!written) return;
          next[k] = toItems(shownQuestions(k, written));
        });
        return next;
      });
      setInvalidKeys(new Set());
      markSaved();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'Could not save the questions.',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionBody
      measure="events"
      footer={
        <SaveBar
          dirty={dirty}
          saving={saving}
          error={error}
          onSave={save}
          onDiscard={() => {
            setDrafts({
              events: toItems(shownQuestions('events', savedLists.events)),
              catering: toItems(
                shownQuestions('catering', savedLists.catering),
              ),
            });
            setInvalidKeys(new Set());
            setError(null);
          }}
          summary={changedKinds
            .map(
              (k) =>
                `${k === 'events' ? 'Event' : 'Catering'} questions changed`,
            )
            .join(' · ')}
          saveLabel="Save questions"
        />
      }
    >
      <SectionHead title="Events & catering" />
      <div className="mt-6">
        <QueryGate query={info} skeleton={<BlockSkeleton rows={5} />}>
          {(store) => (
            <>
              {(['events', 'catering'] as Kind[]).map((kind, i) => (
                <RequestsBlock
                  key={kind}
                  kind={kind}
                  first={i === 0}
                  store={store}
                  items={drafts[kind]}
                  invalidKeys={invalidKeys}
                  savedAt={changedKinds.length === 0 ? savedAt : null}
                  onChange={(items) => {
                    setDrafts((d) => ({ ...d, [kind]: items }));
                    setError(null);
                  }}
                  onClearInvalid={(key) =>
                    setInvalidKeys((s) => {
                      if (!s.has(key)) return s;
                      const n = new Set(s);
                      n.delete(key);
                      return n;
                    })
                  }
                />
              ))}
            </>
          )}
        </QueryGate>
      </div>
    </SectionBody>
  );
};

const LockedQuestion: React.FC = () => (
  <div className="flex items-start gap-2 border-b border-v2-line/70 py-2">
    <span
      aria-hidden
      className="mt-0.5 inline-flex h-7 w-6 shrink-0 cursor-not-allowed items-center justify-center rounded-v2 text-v2-fg-4 opacity-50"
    >
      <IconGrip size={14} />
    </span>
    <span
      aria-hidden
      className="v2-num mt-1.5 w-4 shrink-0 text-right text-v2-12 text-v2-fg-3"
    >
      1
    </span>
    <div className="flex min-w-0 flex-1 items-start gap-2">
      <div className="min-w-0 flex-1">
        <Input
          size="sm"
          aria-label="Question 1"
          value={LOCKED_QUESTION}
          disabled
          readOnly
        />
      </div>
      <span title={LOCKED_DELETE_REASON} className="inline-flex">
        <IconButton size="sm" label="Remove question 1" disabled>
          <IconTrash size={14} />
        </IconButton>
      </span>
    </div>
  </div>
);

const RequestsBlock: React.FC<{
  kind: Kind;
  first: boolean;
  store: StoreInfoData;
  items: Item[];
  invalidKeys: Set<number>;
  savedAt: number | null;
  onChange: (items: Item[]) => void;
  onClearInvalid: (key: number) => void;
}> = ({
  kind,
  first,
  store,
  items,
  invalidKeys,
  savedAt,
  onChange,
  onClearInvalid,
}) => {
  const storeActions = useStoreActions();
  const meta = KIND[kind];
  const enabled = Boolean(store[meta.flag]);
  const [toggling, setToggling] = useState(false);
  const [toggleError, setToggleError] = useState<string | null>(null);
  const [toggledAt, markToggled] = useSavedAt();

  const toggle = async (next: boolean) => {
    setToggling(true);
    setToggleError(null);
    try {
      await storeActions.update({ [meta.flag]: next });
      markToggled();
    } catch (e) {
      setToggleError(
        e instanceof Error
          ? e.message
          : `Could not update the ${meta.noun} setting.`,
      );
    } finally {
      setToggling(false);
    }
  };

  const atMax = items.length >= MAX_CUSTOM_QUESTIONS;

  return (
    <Block
      first={first}
      title={meta.title}
      titleClassName="text-v2-15"
      description={enabled ? undefined : meta.off}
      control={
        <>
          <SavedNote when={toggledAt}>{enabled ? 'On.' : 'Off.'}</SavedNote>
          <SavedNote when={savedAt} />
          <Switch
            checked={enabled}
            onChange={(v) => void toggle(v)}
            label={meta.switchLabel}
            saving={toggling}
          />
        </>
      }
    >
      {toggleError && !enabled && (
        <Callout tone="danger" className="mb-3 max-w-[720px]">
          {toggleError}
        </Callout>
      )}
      {enabled && (
        <div className="max-w-[720px]">
          {toggleError && <Callout tone="danger">{toggleError}</Callout>}
          <div
            className={
              toggleError
                ? 'mt-3 border-t border-v2-line/70'
                : 'border-t border-v2-line/70'
            }
          >
            <LockedQuestion />
            {items.length > 0 && (
              <ReorderableList
                aria-label={`${meta.title} questions`}
                items={items}
                numbered
                numberFrom={2}
                keyOf={(i) => i.key}
                itemLabel={(i, index) =>
                  i.text.trim() ? `“${i.text.trim()}”` : `question ${index + 2}`
                }
                onMove={(from, to) => {
                  const next = [...items];
                  const [moved] = next.splice(from, 1);
                  next.splice(to, 0, moved);
                  onChange(next);
                }}
                renderItem={(item, index) => {
                  const invalid = invalidKeys.has(item.key);
                  const number = index + 2;
                  return (
                    <div className="flex items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <Input
                          size="sm"
                          aria-label={`Question ${number}`}
                          aria-invalid={invalid || undefined}
                          value={item.text}
                          invalid={invalid}
                          placeholder={meta.placeholder}
                          onChange={(e) => {
                            onChange(
                              items.map((i) =>
                                i.key === item.key
                                  ? { ...i, text: e.target.value }
                                  : i,
                              ),
                            );
                            if (e.target.value.trim()) onClearInvalid(item.key);
                          }}
                        />
                        {invalid && (
                          <p
                            role="alert"
                            className="mt-1 text-v2-12 text-v2-danger"
                          >
                            Question can't be empty.
                          </p>
                        )}
                      </div>
                      <IconButton
                        size="sm"
                        label={`Remove question ${number}`}
                        onClick={() =>
                          onChange(items.filter((i) => i.key !== item.key))
                        }
                        className="hover:text-v2-danger"
                      >
                        <IconTrash size={14} />
                      </IconButton>
                    </div>
                  );
                }}
              />
            )}
          </div>
          <div className="mt-3 flex items-center gap-3">
            <Button
              size="sm"
              variant="ghost"
              leading={<IconPlus size={14} />}
              onClick={() => onChange([...items, { key: nextKey++, text: '' }])}
              disabledReason={
                atMax
                  ? `Up to ${MAX_CUSTOM_QUESTIONS} custom questions.`
                  : undefined
              }
            >
              Add question
            </Button>
            <Reason>
              {atMax
                ? `That's the maximum of ${MAX_CUSTOM_QUESTIONS} custom questions. Remove one to add another.`
                : `${items.length} of ${MAX_CUSTOM_QUESTIONS}. Drag the grip or use the arrow keys to reorder.`}
            </Reason>
          </div>
        </div>
      )}
    </Block>
  );
};

export default EventsSection;
